/**
 * Bandeau « Ajouter à l'écran d'accueil », sur téléphone.
 *
 * Android/Chrome : le navigateur émet `beforeinstallprompt` quand le site est
 * installable (manifeste + icônes) ; on retient l'évènement et le bouton
 * « Installer » déclenche la boîte de dialogue native.
 *
 * iOS/Safari : pas d'API — on explique le geste (Partager → Sur l'écran
 * d'accueil).
 *
 * Le bandeau ne s'affiche qu'une fois connecté, jamais quand l'app tourne
 * déjà en plein écran (mode standalone), et « Plus tard » le fait taire
 * trente jours.
 */

import { useEffect, useState } from 'react';

const DISMISS_KEY = 'pianoled.analytics.install-dismissed';
const DISMISS_DAYS = 30;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS se présente comme un Mac, mais avec un écran tactile.
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function dismissedRecently(): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY) ?? 0);
    return Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

function rememberDismiss() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // navigation privée : le bandeau reviendra, ce n'est pas grave
  }
}

export function InstallBanner() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;
    // Téléphone ou tablette seulement : un écran tactile étroit.
    const mobile = window.matchMedia('(max-width: 900px) and (pointer: coarse)').matches;
    if (!mobile) return;

    if (isIos()) {
      setIos(true);
      setVisible(true);
      return;
    }
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setVisible(false));
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (!visible) return null;

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setPrompt(null);
  };

  const later = () => {
    rememberDismiss();
    setVisible(false);
  };

  return (
    <div className="install" role="dialog" aria-label="Installer l'application">
      <span className="app-header__dot" aria-hidden="true" />
      <div className="install__text">
        <strong>Ajouter à l'écran d'accueil</strong>
        {ios ? (
          <span className="install__hint">
            Dans Safari : bouton <span aria-hidden="true">⎙</span> Partager, puis « Sur l'écran d'accueil ».
          </span>
        ) : (
          <span className="install__hint">Ouvre le dashboard comme une appli, en plein écran.</span>
        )}
      </div>
      <div className="install__actions">
        {!ios && (
          <button type="button" className="btn btn--primary btn--small" onClick={() => void install()}>
            Installer
          </button>
        )}
        <button type="button" className="btn btn--small" onClick={later}>
          Plus tard
        </button>
      </div>
    </div>
  );
}
